import React from "react";
import { Button, Col, ListGroup, Row } from "react-bootstrap";
import { defineMessages, useIntl } from "react-intl";

import genericTranslations from "../../utils/genericTranslations.js";

import SupportIssue from "./components/SupportIssue.jsx";
import "./Support.scss";

const messages = defineMessages({
  content: {
    id: "modules.support.Support.content",
    description: "Support content",
    defaultMessage:
      "On this page you can find various solutions to common issues. If you\n" +
      "            experience a problem that is not listed, please contact the Drive!\n" +
      "            support team using the button below.",
  },
  loginInstructions: {
    id: "modules.support.Support.loginInstructions",
    description: "Login Instructions",
    defaultMessage:
      "  You can log in to the Drive! app using the credentials provided.\n" +
      "            After your first successful login, you must set up an access code\n" +
      "            that will be used for subsequent logins.",
  },
  loginTroubleshoot: {
    id: "modules.support.Support.loginTroubleshoot",
    description: "Login Troubleshoot",
    defaultMessage: "Troubleshooting Login Issues",
  },
  forgotPassword: {
    id: "modules.support.Support.forgotPassword",
    description: "Forgot your password",
    defaultMessage: "I forgot my password",
  },
  forgotPasswordInstructions: {
    id: "modules.support.Support.forgotPasswordInstructions",
    description: "Forgot your password",
    defaultMessage:
      'Use the "Forgot Password?" option on the login screen to request a\n' +
      "              password reset. Enter your email address on the next screen, and\n" +
      "              if your email is registered, you will receive a reset code to\n" +
      "              create a new password.",
  },
  forgotAccessCode: {
    id: "modules.support.Support.forgotAccessCode",
    description: "Forgot your access code",
    defaultMessage: "I forgot my access code",
  },
  forgotAccessCodeInstructions: {
    id: "modules.support.Support.forgotAccessCodeInstructions",
    description: "Forgot your access code",
    defaultMessage:
      "At this moment, we do not offer any options in the app to recover a forgotten access code. This is because the access code is only stored on your device. To solve this problem, all app data must be deleted from your phone, after which you can log in again.",
  },
  loginFailed: {
    id: "modules.support.Support.loginFailed",
    description: "Login failed.",
    defaultMessage: 'I keep getting "Something went wrong while logging in!"',
  },
  loginFailedInstructions: {
    id: "modules.support.Support.loginFailedInstructions",
    description: "Failed login",
    defaultMessage:
      "There may be several problems with this message. Your account may have been disabled (by mistake), your\n" +
      "\n" +
      "entered username may be unknown, your connection to our servers may be\n" +
      "\n" +
      "failing, etc. In order not to leak account data to third parties, we\n" +
      "\n" +
      "give this general error message in such cases. This means that someone with\n" +
      "\n" +
      "malicious intentions cannot find out whether someone has an account with us or not." +
      "\n" +
      "\n" +
      "If you believe this is incorrect, please contact us using the button at the top of the page.",
  },
  contactUs: {
    id: "modules.support.Support.contactUs",
    description: "Contact us",
    defaultMessage:
      "If you believe this is incorrect, please contact us using the button at the top of the page.",
  },
  scanQRCode: {
    id: "modules.support.Support.scanQRCode",
    description: "Scan QR Code",
    defaultMessage: "Scanning QR Codes",
  },
  scanQRCodeInstructions: {
    id: "modules.support.Support.scanQRCodeInstructions",
    description: "Scan QR Code instructions",
    defaultMessage:
      "Several modules in the Drive! app use your device’s camera to scan\n" +
      "            QR codes. These codes are found on vehicles so you can easily check\n" +
      "            for malfunctions of a vehicle by scanning the corresponding QR code.",
  },
  scanQRCodeTroubleshoot: {
    id: "modules.support.Support.scanQRCodeTroubleshoot",
    description: "Scan QR code troubleshooting",
    defaultMessage: "Troubleshooting QR Code Issues",
  },
  cameraAccessIssue: {
    id: "modules.support.Support.cameraAccessIssue",
    description: "Camera access issue",
    defaultMessage: "I get a black screen when scanning a QR code",
  },
  cameraAccess: {
    id: "modules.support.Support.cameraAccess",
    description: "Access camera instructions",
    defaultMessage:
      "This can happen if you have disabled camera permissions for the Drive! app. You can grant these from the appropriate settings screens of your mobile OS.",
  },
  cameraAccessiOS: {
    id: "modules.support.Support.cameraAccessiOS",
    description: "Access camera instructions iOS",
    defaultMessage: "On iOS, go to Settings \u003e Drive! \u003e Camera.",
  },
  cameraAccessAndroid: {
    id: "modules.support.Support.cameraAccessAndroid",
    description: "Access camera instructions Android",
    defaultMessage:
      "On Android, go to Settings \u003e Apps 003e Drive! \u003e Permissions \u003e Camera.",
  },
});
const SupportLayout = () => {
  const intl = useIntl();

  return (
    <Row>
      <Col sm={{ span: 8, offset: 2 }}>
        <div className="support-container">
          <h1>Arriva Drive! Support</h1>
          <p>{intl.formatMessage(messages.content)}</p>
          <p>
            <Button variant="primary" href="mailto:drive@arriva.nl">
              E-mail support
            </Button>
          </p>
        </div>

        <div className="support-container">
          <h3>{intl.formatMessage(genericTranslations.login)}</h3>
          <p>{intl.formatMessage(messages.loginInstructions)}</p>

          <h4>{intl.formatMessage(messages.loginTroubleshoot)}</h4>
          <ListGroup>
            <SupportIssue title={intl.formatMessage(messages.forgotPassword)}>
              {intl.formatMessage(messages.forgotPasswordInstructions)}
            </SupportIssue>
            <SupportIssue title={intl.formatMessage(messages.forgotAccessCode)}>
              {intl.formatMessage(messages.forgotAccessCodeInstructions)}
            </SupportIssue>
            <SupportIssue title={intl.formatMessage(messages.loginFailed)}>
              {intl.formatMessage(messages.loginFailedInstructions)}
              <br />
              <br />
              {intl.formatMessage(messages.contactUs)}
            </SupportIssue>
          </ListGroup>
        </div>

        <div className="support-container">
          <h3>{intl.formatMessage(messages.scanQRCode)}</h3>
          <p>{intl.formatMessage(messages.scanQRCodeInstructions)}</p>
          <h4>{intl.formatMessage(messages.scanQRCodeTroubleshoot)}</h4>
          <ListGroup>
            <SupportIssue
              title={intl.formatMessage(messages.cameraAccessIssue)}
            >
              <p>{intl.formatMessage(messages.cameraAccess)}</p>
              <p>{intl.formatMessage(messages.cameraAccessiOS)}</p>
              <p>{intl.formatMessage(messages.cameraAccessAndroid)}</p>
            </SupportIssue>
          </ListGroup>
        </div>
      </Col>
    </Row>
  );
};

export default SupportLayout;
